import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCreditBalance } from '../services/creditsService';
import { useAuth } from '../contexts/AuthContext';

interface CreditBalanceProps {
  variant?: 'default' | 'compact' | 'icon-only';
  className?: string;
}

const CreditBalance: React.FC<CreditBalanceProps> = ({
  variant = 'default',
  className = ''
}) => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Check authentication before fetching credit balance
    if (!isAuthenticated) {
      setBalance(0);
      setLoading(false);
      return;
    }

    // Initial fetch of credit balance
    const fetchBalance = async () => {
      try {
        setLoading(true);
        const result = await getCreditBalance();
        setBalance(result);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
        setBalance(0);
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();

    // Set up an interval to refresh the balance every 10 seconds
    const intervalId = setInterval(() => {
      // Only fetch if authenticated
      if (isAuthenticated) {
        fetchBalance();
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]); // Re-run when authentication state changes

  // Format the balance safely
  const formattedBalance = (balance === null || isNaN(Number(balance)))
    ? '0'
    : Number(balance).toFixed(0);

  if (loading) {
    return <span className="animate-pulse">Loading...</span>;
  }

  if (variant === 'icon-only') {
    return (
      <Link
        to="/account?tab=credits"
        className={`relative flex items-center justify-center ${className}`}
        title={`Credits: ${formattedBalance}`}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[var(--color-muted)] hover:text-white transition-colors" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to="/account?tab=credits"
        className={`flex items-center gap-1 text-sm text-[var(--color-muted)] hover:text-white transition-colors ${className}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>{formattedBalance}</span>
      </Link>
    );
  }

  return (
    <Link
      to="/account?tab=credits"
      className={`flex items-center gap-2 hover:bg-[#7F56D9]/20 px-2 py-1 rounded transition-colors ${className}`}
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-[#7F56D9] to-[#9D4EDD]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div>
        <p className="text-xs text-[var(--color-muted)]">Credits</p>
        <p className="font-semibold text-white">{formattedBalance}</p>
      </div>
    </Link>
  );
};

export default CreditBalance;
